import React from 'react';
import { ArrowUpRight, LayoutGrid, Grid3x3 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { proposalTokens } from './tokens';

interface Destination {
  id: string;
  city: string;
  country: string;
  heroImage?: string;
  tagline?: string;
}

type LayoutType = 'masonry' | 'two-column' | 'multi-column';

interface OverviewProps {
  eventName: string;
  clientName: string;
  destinations: Destination[];
  layoutType?: LayoutType;
  onNavigate: (destinationId: string) => void;
}

/**
 * Overview component - Destination grid overview page
 * Adapted from template4 Overview component, following UI/UX guidelines
 */
export function Overview({ eventName, clientName, destinations, layoutType = 'multi-column', onNavigate }: OverviewProps) {


  // Destination Card Component (reusable)
  const DestinationCard: React.FC<{ 
    destination: Destination; 
    className?: string;
    height?: string 
  }> = ({ destination, className = '', height = 'h-[280px] md:h-[400px]' }) => {
    const heroImage = destination.heroImage || 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1200&q=80';
    
    return (
      <div 
        onClick={() => onNavigate(destination.id)}
        className={cn(
          className, height,
          "group relative rounded-3xl overflow-hidden cursor-pointer",
          "border border-black/10 shadow-lg",
          "transition-transform hover:scale-[1.02]"
        )}
        style={{ fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}
      >
        <img
          src={heroImage}
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
          alt={`${destination.city}, ${destination.country}`}
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-90"></div>
        <div className="absolute bottom-8 left-8 max-w-xl">
          <h2 className="text-4xl font-serif font-bold text-white mb-2 flex items-center" style={{ fontFamily: proposalTokens.typography.fontFamily.serif.join(', ') }}>
            {destination.city} <ArrowUpRight className="ml-4 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: proposalTokens.colors.gold }} />
          </h2>
          {destination.tagline && (
            <p className="text-white/80 text-lg line-clamp-2">{destination.tagline}</p>
          )}
          {!destination.tagline && (
            <p className="text-white/80 text-lg">{destination.country}</p>
          )}
        </div>
      </div>
    );
  };

  // Layout 0: Masonry (original - good for 3)
  const renderMasonryLayout = () => {
    if (destinations.length < 3) {
      // Fallback to two-column if less than 3
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {destinations.map((dest) => (
            <DestinationCard 
              key={dest.id}
              destination={dest}
              height="h-[280px] sm:h-[500px]"
            />
          ))}
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 auto-rows-[280px] md:auto-rows-[400px]">
        {/* Dest 1 - Large */}
        <DestinationCard 
          destination={destinations[0]}
          className="md:col-span-8"
          height="h-[280px] md:h-[400px]"
        />

        {/* Dest 2 - Tall/Side */}
        <div 
          onClick={() => onNavigate(destinations[1].id)}
          className="md:col-span-4 md:row-span-2 group relative rounded-3xl overflow-hidden cursor-pointer border border-black/10 shadow-lg h-[280px] md:h-auto transition-transform hover:scale-[1.02]"
        >
          <img
            src={destinations[1].heroImage || 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1200&q=80'}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            alt={`${destinations[1].city}, ${destinations[1].country}`}
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent opacity-90"></div>
          <div className="absolute bottom-8 left-8 max-w-xs">
            <h2 className="text-4xl font-serif font-bold text-white mb-2 flex items-center" style={{ fontFamily: proposalTokens.typography.fontFamily.serif.join(', ') }}>
              {destinations[1].city} <ArrowUpRight className="ml-4 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: proposalTokens.colors.gold }} />
            </h2>
            {destinations[1].tagline ? (
              <p className="text-white/80 line-clamp-3">{destinations[1].tagline}</p>
            ) : (
              <p className="text-white/80">{destinations[1].country}</p>
            )}
          </div>
        </div>

        {/* Dest 3 - Wide/Bottom */}
        {destinations[2] && (
          <DestinationCard 
            destination={destinations[2]}
            className="md:col-span-8"
            height="h-[280px] md:h-[400px]"
          />
        )}

        {/* Additional destinations beyond 3 */}
        {destinations.slice(3).map((dest) => (
          <DestinationCard 
            key={dest.id}
            destination={dest}
            className="md:col-span-6"
            height="h-[280px] md:h-[400px]"
          />
        ))}
      </div>
    );
  };

  // Layout 1: Two-column grid (half and half)
  const renderTwoColumnLayout = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {destinations.map((dest) => (
        <DestinationCard 
          key={dest.id}
          destination={dest}
          height="h-[280px] sm:h-[420px]"
        />
      ))}
    </div>
  );

  // Layout 2: Multi-column vertical (responsive: 4 cols desktop, 2x2 tablet, 1 mobile)
  const renderMultiColumnLayout = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {destinations.map((dest) => (
        <DestinationCard 
          key={dest.id}
          destination={dest}
          height="h-[280px] sm:h-[400px]"
        />
      ))}
    </div>
  );

  const renderCurrentLayout = () => {
    switch (layoutType) {
      case 'masonry':
        return renderMasonryLayout();
      case 'two-column':
        return renderTwoColumnLayout();
      case 'multi-column':
        return renderMultiColumnLayout();
    }
  };

  return (
    <div className="h-full overflow-y-auto px-6 md:px-12 py-8 md:py-12 hide-scrollbar relative" style={{ backgroundColor: proposalTokens.colors.cream }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-16 max-w-5xl animate-fade-in">
          <span className="inline-block px-3 py-1 rounded-full border border-black/20 text-xs font-medium mb-6" style={{ color: `${proposalTokens.colors.teal}99`, fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}>
            PREPARED FOR {clientName.toUpperCase()}
          </span>
          <h1 className="overview-event-title font-serif font-medium mb-8 tracking-tight" style={{ color: proposalTokens.colors.teal, fontFamily: proposalTokens.typography.fontFamily.serif.join(', ') }}>
            {eventName}
          </h1>
        </div>

        {/* Dynamic Layout */}
        {destinations.length > 0 ? (
          renderCurrentLayout()
        ) : (
          <div className="text-center py-16">
            <p className="text-teal/60 text-lg" style={{ color: `${proposalTokens.colors.teal}99`, fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}>
              No destinations available
            </p>
          </div>
        )}

      </div>
    </div>
  );
}

